'use client';

import { useEffect, useMemo, useRef } from 'react';

import {
  animate,
  useMotionValue,
  useReducedMotion,
  useTransform,
  type AnimationPlaybackControls,
} from 'framer-motion';
import { stegaClean } from '@sanity/client/stega';

import { MotionP, MotionSpan } from '@/components/global/utils/motionTags';

/**
 * Splits the authored stat into a countable figure plus whatever brackets it, so
 * "500+" and "€2000" still climb.
 *
 * Only an unambiguous integer qualifies: bare digits, or German thousands
 * grouping where every "." is followed by exactly three digits. Anything else
 * renders as authored, because "2,5" and "2.500" cannot be told apart without
 * knowing which the editor meant — and guessing wrong would animate to a number
 * a hundredfold off.
 */
function parseValue(value: string) {
  const match = /^(\D*)(\d{1,3}(?:\.\d{3})+|\d+)(\D*)$/.exec(value.trim());
  if (!match) return null;

  const [, prefix, digits, suffix] = match;
  const target = Number(digits.replace(/\./g, ''));
  if (!Number.isFinite(target)) return null;

  return { prefix, suffix, target, grouped: digits.includes('.') };
}

// the page is German, so a grouped figure regroups as it climbs rather than
// spilling into an unseparated run of digits
const formatCount = (value: number, grouped: boolean) =>
  grouped
    ? Math.round(value).toLocaleString('de-DE')
    : String(Math.round(value));

/**
 * The stat counts up the first time it scrolls into view. The figure lives in a
 * MotionValue driven straight onto textContent, so it ticks every frame without
 * re-rendering React — which is why the motion span holds nothing else.
 */
export default function StatValue({
  value,
  className,
}: {
  value?: string;
  className?: string;
}) {
  // stega adds invisible characters to strings in draft mode, which would make
  // the number unparseable and drop the stat back to plain text in preview
  const clean = stegaClean(value) ?? '';
  const parsed = useMemo(() => parseValue(clean), [clean]);

  const reduceMotion = useReducedMotion();

  const count = useMotionValue(0);
  const text = useTransform(count, (latest) =>
    formatCount(latest, parsed?.grouped ?? false),
  );

  const controls = useRef<AnimationPlaybackControls | null>(null);
  useEffect(() => () => controls.current?.stop(), []);

  const startCount = () => {
    if (!parsed) return;

    if (reduceMotion) {
      count.set(parsed.target);
      return;
    }

    // eased out hard: the figure covers most of its range early and then settles,
    // so it reads as landing on the number rather than crawling to it
    controls.current = animate(count, parsed.target, {
      duration: 2,
      ease: [0.16, 1, 0.3, 1],
    });
  };

  if (!parsed) return <p className={className}>{value}</p>;

  return (
    <MotionP
      className={className}
      // once: the count belongs to arriving at the section, not to every pass over it
      viewport={{ once: true, margin: '-15% 0px' }}
      onViewportEnter={startCount}
    >
      {/* the authored figure stays available to screen readers and to a
          crawler that never runs the animation, which only ever sees "0" */}
      <span className="sr-only">{value}</span>

      <span aria-hidden>
        {parsed.prefix}
        <MotionSpan>{text}</MotionSpan>
        {parsed.suffix}
      </span>
    </MotionP>
  );
}
